import { useEffect, useRef } from "react";
import { useCharacter } from "../../character";
import { CharacterFlow } from "../../character/types";
import { DEFAULT_CHAT_LANGUAGE, useUserInfo, useUserSettings, } from "../../user";
/** This hook is used to load character data when the component mounts.*/
export function useLoadCharacter({ characterId, withTranslation = false, include, }) {
    const useCharacterReturnValues = useCharacter();
    const { resetCharacterData, getCharacterData, characterData, loadingType } = useCharacterReturnValues;
    const { userChatLanguage, areUserSettingsLoaded } = useUserSettings();
    const { isAuthLoaded, isAuthenticated } = useUserInfo();
    const isCharacterLoading = loadingType === CharacterFlow.GET_CHARACTER;
    // getCharacterById.rejected never touches characterData, so a failed fetch leaves it
    // permanently null. Without tracking the attempt here, this effect keeps re-firing
    // (isCharacterLoading flips back to false on rejection) and retries forever, which
    // keeps `characterData` falsy and any caller gating a spinner on it stuck indefinitely.
    // The ref is set before dispatching (not from the settled result) so a rejection's own
    // React re-render can't race this effect into firing a second request for the same id.
    const refAttemptedCharacterId = useRef(null);
    useEffect(() => {
        const shouldLoadCharacter = characterId &&
            !characterData &&
            !isCharacterLoading &&
            refAttemptedCharacterId.current !== characterId;
        if (shouldLoadCharacter) {
            const readyToLoadCharacter = (withTranslation &&
                isAuthLoaded &&
                ((isAuthenticated && areUserSettingsLoaded) || !isAuthenticated)) ||
                !withTranslation;
            if (readyToLoadCharacter) {
                const options = {
                    ...(withTranslation && userChatLanguage !== DEFAULT_CHAT_LANGUAGE
                        ? { language: userChatLanguage }
                        : {}),
                    ...(include?.length ? { include } : {}),
                };
                refAttemptedCharacterId.current = characterId;
                void getCharacterData(characterId, options);
            }
        }
    }, [
        characterId,
        withTranslation,
        include,
        userChatLanguage,
        getCharacterData,
        areUserSettingsLoaded,
        characterData,
        isCharacterLoading,
        isAuthLoaded,
        isAuthenticated,
    ]);
    useEffect(() => {
        return () => {
            resetCharacterData();
        };
    }, []);
    return useCharacterReturnValues;
}
